import { FC, useEffect, useRef, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  TableContainer,
  Box,
  Text,
} from "@chakra-ui/react";
import { useTable, useSortBy } from "react-table";
import { formStyles, scrollBarStyle } from "../../../utils/styles/styles";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import { FixedColumn } from "./FixedColumn";
import { NO_RESULTS } from "../../../utils/constants";
import SpinnerComponent from "../../atom/Spinner/SpinnerComponent";

interface TableComponentProps {
  columns: any[];
  data: any[];
  isLoaded?: boolean;
  columnsStyle?: string[];
  fixedColumnIds?: FixedColumn[];
  editingRowIndex?: string | number | null;
  highlightedRows?: number[];
}

const TableComponent: FC<TableComponentProps> = ({
  columns,
  data,
  isLoaded = true,
  columnsStyle,
  fixedColumnIds,
  editingRowIndex,
  highlightedRows = [],
}) => {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [fixedColumnWidths, setFixedColumnWidths] = useState<number[]>([]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useSortBy,
    );

  const checkScroll = () => {
    if (tableRef.current && tableContainerRef.current) {
      const tableWidth = tableRef.current.scrollWidth;
      const containerWidth = tableContainerRef.current.clientWidth;
      const scrollLeft = tableContainerRef.current.scrollLeft;
      const hasScroll = tableWidth > containerWidth;
      const atEndOfScroll = scrollLeft + containerWidth >= tableWidth;
      setHasScroll(hasScroll && !atEndOfScroll);
    }
  };

  useEffect(() => {
    checkScroll();

    const container = tableContainerRef.current;

    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [data]);

  const hasColSpanGreaterThanOne = columns.some(
    (column) => column.columns && column.columns.length > 1,
  );

  return (
    <Box>
      {isLoaded ? (
        data.length > 0 ? (
          <TableContainer
            ref={tableContainerRef}
            position="relative"
            height="100%"
            sx={{
              ...scrollBarStyle,
            }}
          >
            <Table ref={tableRef} {...getTableProps()} variant="striped">
              <Thead>
                {headerGroups.map((headerGroup, headerIndex) => (
                  <Tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id || headerIndex}
                  >
                    {headerGroup.headers.map((column) => (
                      <TableHeader
                        key={column.id}
                        column={column}
                        columnsStyle={columnsStyle}
                        fixedColumnIds={fixedColumnIds}
                        hasScroll={hasScroll}
                        hasColSpanGreaterThanOne={hasColSpanGreaterThanOne}
                        setFixedColumnWidths={setFixedColumnWidths}
                        fixedColumnWidths={fixedColumnWidths}
                      />
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {rows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <TableRow
                      key={row.id}
                      row={row}
                      fixedColumnIds={fixedColumnIds}
                      rowIndex={rowIndex}
                      columnsStyle={columnsStyle}
                      hasScroll={hasScroll}
                      fixedColumnWidths={fixedColumnWidths}
                      editingRowIndex={editingRowIndex}
                      isHighlighted={highlightedRows.includes(rowIndex)}
                    />
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            sx={formStyles}
          >
            <Text m={4} fontSize="sm">
              {NO_RESULTS}
            </Text>
          </Box>
        )
      ) : (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          sx={formStyles}
          border="none"
        >
          <SpinnerComponent thickness="3px" size="xl" />
        </Box>
      )}
    </Box>
  );
};
export default TableComponent;
